import React from 'react';
import { Helmet } from 'react-helmet-async';
import { LegalEntityDetails } from '../components/common/LegalEntityDetails';
import { registrationIdentifiers } from '../lib/jsonLd';

export function PrivacyPolicyPage() {
  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Fundacja Znajdki',
    legalName: 'Fundacja „Znajdki"',
    url: 'https://znajdki.pl',
    taxID: '1132860384',
    vatID: 'PL1132860384',
    identifier: registrationIdentifiers,
    sameAs: [
      'https://www.facebook.com/znajdki/',
      'https://www.instagram.com/fundacja_znajdki/'
    ]
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
      <Helmet>
        <title>Polityka prywatności | Fundacja "Znajdki"</title>
        <meta name="description" content="Polityka prywatności Fundacji Znajdki. Informacje o przetwarzaniu danych osobowych, podstawach prawnych, celach i prawach osób, których dane dotyczą." />
        <meta property="og:title" content="Polityka prywatności | Fundacja Znajdki" />
        <meta property="og:description" content="Informacje o przetwarzaniu danych osobowych w Fundacji Znajdki." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      </Helmet>

      <header className="mb-10">
        <h1 className="heading-1">Polityka prywatności</h1>
        <p className="body-medium mt-4 text-accessible-gray-600 dark:text-accessible-gray-300 high-contrast:text-white">
          Obowiązuje od: 08.09.2025
        </p>
      </header>

      <section className="space-y-8 text-accessible-gray-700 dark:text-accessible-gray-200 high-contrast:text-white">
        <LegalEntityDetails />

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Administrator danych</h2>
          <p>
            Administratorem danych osobowych jest Fundacja Znajdki, ul. Leona Berensona 90, 03-287 Warszawa, e-mail:
            <a className="underline ml-1" href="mailto:znajdki2012@gmail.com">znajdki2012@gmail.com</a>.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Zakres i cele przetwarzania</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Obsługa zapytań z formularza kontaktowego i wiadomości e-mail.</li>
            <li>Realizacja darowizn, rozliczenia i obowiązki księgowe.</li>
            <li>Komunikacja informacyjna i marketingowa za zgodą, w tym wysyłka newslettera.</li>
            <li>Organizacja wolontariatu i adopcji zwierząt.</li>
          </ul>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Podstawy prawne</h2>
          <p>
            Dane przetwarzamy zgodnie z RODO: art. 6 ust. 1 lit. a (zgoda), b (umowa), c (obowiązek prawny), f (prawnie uzasadniony interes Administratora).
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Odbiorcy danych</h2>
          <p>
            Dane mogą być przekazywane podmiotom przetwarzającym na nasze zlecenie (np. dostawcy hostingu, systemy płatności), wyłącznie na podstawie umów powierzenia i w niezbędnym zakresie.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Newsletter - Mailchimp</h2>
          <p className="mb-3">
            W celu wysyłki newslettera korzystamy z usługi Mailchimp, świadczonej przez The Rocket Science Group LLC d/b/a Mailchimp, 675 Ponce de Leon Ave NE, Suite 5000, Atlanta, GA 30308 USA.
          </p>
          <p className="mb-3">
            <strong>Zakres danych:</strong> W ramach zapisu na newsletter zbieramy adres e-mail oraz opcjonalnie imię. Dane te są przesyłane i przechowywane na serwerach Mailchimp.
          </p>
          <p className="mb-3">
            <strong>Cel przetwarzania:</strong> Dane przetwarzamy wyłącznie w celu wysyłki newslettera zawierającego informacje o działalności Fundacji, naszych podopiecznych oraz organizowanych akcjach.
          </p>
          <p className="mb-3">
            <strong>Podstawa prawna:</strong> Przetwarzanie odbywa się na podstawie udzielonej zgody (art. 6 ust. 1 lit. a RODO), która może zostać wycofana w każdej chwili poprzez kliknięcie linku rezygnacji w każdym newsletterze lub kontakt z Administratorem.
          </p>
          <p className="mb-3">
            <strong>Przekazanie danych do państw trzecich:</strong> Mailchimp przetwarza dane na serwerach zlokalizowanych również w Stanach Zjednoczonych. Przekazanie danych odbywa się na podstawie standardowych klauzul umownych zatwierdzonych przez Komisję Europejską. Więcej informacji: 
            <a className="underline ml-1" href="https://www.intuit.com/privacy/statement/" target="_blank" rel="noopener noreferrer">polityka prywatności Mailchimp</a>.
          </p>
          <p className="mb-3">
            <strong>Rezygnacja z newslettera:</strong> W każdej chwili możesz zrezygnować z otrzymywania newslettera, klikając w link rezygnacji znajdujący się w stopce każdej wiadomości lub kontaktując się z nami na adres 
            <a className="underline ml-1" href="mailto:znajdki2012@gmail.com">znajdki2012@gmail.com</a>.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Okres przechowywania</h2>
          <p>
            Dane przechowujemy przez czas niezbędny do realizacji celów, a następnie przez okres wymagany przepisami (np. rachunkowość) lub do czasu wycofania zgody.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Prawa użytkownika</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Dostęp do danych, ich sprostowanie, usunięcie lub ograniczenie przetwarzania.</li>
            <li>Prawo sprzeciwu wobec przetwarzania oraz przenoszenia danych.</li>
            <li>Wycofanie zgody w dowolnym momencie bez wpływu na zgodność z prawem przetwarzania przed wycofaniem.</li>
            <li>Skarga do Prezesa UODO.</li>
          </ul>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Przekazywanie poza EOG</h2>
          <p>
            Jeżeli dane są przekazywane poza EOG, odbywa się to na podstawie odpowiednich zabezpieczeń, np. standardowych klauzul umownych.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Pliki cookies</h2>
          <p>
            Szczegółowe informacje znajdują się na stronie <a className="underline" href="/polityka-cookies">Polityka ciasteczek</a>.
          </p>
        </div>

        <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-6 rounded-lg">
          <h2 className="heading-4 mb-3">Kontakt w sprawach danych osobowych</h2>
          <p>
            W sprawach związanych z danymi osobowymi prosimy o kontakt: 
            <a className="underline ml-1" href="mailto:znajdki2012@gmail.com">znajdki2012@gmail.com</a> lub listownie na adres fundacji.
          </p>
        </div>
      </section>
    </div>
  );
}


